import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Trending.css";
import Loader from "./Loader";
import AuthModal from "./AuthModal";
import { toast } from "react-hot-toast";

const API_BASE = "https://api.mariamlyluxe.com/public/api";
const CART_URL = "https://api.mariamlyluxe.com/public/api/cart";
const PLACEHOLDER = null;

// Custom hook to check if there are hot offer products
export function useHotOffersCheck() {
  const [hasHotOffers, setHasHotOffers] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${API_BASE}/products/hot-offers`;
        const r = await fetch(url, { headers: { Accept: "application/json" } });
        const j = await r.json();
        const hasProducts = Array.isArray(j?.hot_offer_products) && j.hot_offer_products.length > 0;
        if (!cancelled) setHasHotOffers(hasProducts);
      } catch (error) {
        console.error('Error checking hot offers:', error);
        if (!cancelled) setHasHotOffers(false);
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { hasHotOffers, isChecking };
}

export default function BrandsSlider({
  title = "Hot Offers",
  subtitle = "Don't miss out on our hot offers limited time, big savings!",
  viewAllHref = "/offers",
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perPage, setPerPage] = useState();
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const viewportRef = useRef(null);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX - touchEndX > 50) {
      goToNext();
    } else if (touchEndX - touchStartX > 50) {
      goToPrev();
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${API_BASE}/products/hot-offers`;
        const r = await fetch(url, { headers: { Accept: "application/json" } });
        const j = await r.json();
        const list = Array.isArray(j?.hot_offer_products) ? j.hot_offer_products : [];
        list.sort((a, b) => {
          const da = a?.updated_at ? new Date(a.updated_at).getTime() : 0;
          const db = b?.updated_at ? new Date(b.updated_at).getTime() : 0;
          return db - da;
        });
        const norm = list.map((p) => ({
          id: p.id,
          name: p.name ?? "Product",
          selling_price: Number(p.selling_price ?? p.price ?? 0),
          regular_price: Number(p.regular_price ?? 0),
          variants: p.variants || [],
          image_url: p.image_url || PLACEHOLDER,
        }));
        if (!cancelled) setItems(norm);
      } catch (error) {
        console.error("Error fetching hot offer products:", error);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const m480 = window.matchMedia("(max-width: 480px)");
    const m768 = window.matchMedia("(max-width: 768px)");
    const m1024 = window.matchMedia("(max-width: 1024px)");
    const apply = () => {
      if (m480.matches) setPerPage(2);
      else if (m768.matches) setPerPage(2);
      else if (m1024.matches) setPerPage(3);
      else setPerPage(4);
    };
    apply();
    [m480, m768, m1024].forEach((m) => m.addEventListener("change", apply));
    return () => [m480, m768, m1024].forEach((m) => m.removeEventListener("change", apply));
  }, []);

  const slides = useMemo(() => {
    const src = loading
      ? Array.from({ length: (perPage || 1) * 2 }, (_, i) => ({ __sk: i }))
      : items;
    const out = [];
    const step = Math.max(1, perPage || 1);
    for (let i = 0; i < src.length; i += step) out.push(src.slice(i, i + step));
    return out.length ? out : [[]];
  }, [items, perPage, loading]);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onScroll = () => setPage(Math.round(vp.scrollLeft / vp.clientWidth));
    vp.addEventListener("scroll", onScroll, { passive: true });
    return () => vp.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (i) => {
    const vp = viewportRef.current;
    if (!vp) return;
    vp.scrollTo({ left: i * vp.clientWidth, behavior: "smooth" });
    setPage(i);
  };

  const goToPrev = () => {
    const prevPage = Math.max(0, page - 1);
    goTo(prevPage);
  };

  const goToNext = () => {
    const nextPage = Math.min(slides.length - 1, page + 1);
    goTo(nextPage);
  };

  const getToken = () =>
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("auth_token");

  async function handleAddToCart(productId, quantity = 1) {
    const token = getToken();
    if (!token) {
      setShowAuthModal(true);
      return;
    }
    try {
      setBusyId(productId);
      const res = await fetch(CART_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ product_id: productId, quantity }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to add to cart");
      }
      toast.success("Product added to cart");
    } catch (err) {
      toast.error(err.message || "Could not add to cart");
    } finally {
      setBusyId(null);
    }
  }

  const handleLoginSuccess = () => setShowAuthModal(false);

  if (!loading && items.length === 0) {
    return null;
  }

  return (
    <>
      <section
        className="trd"
        style={{ "--page": page, "--pages": slides.length }}
        aria-label="Hot offers slider"
      >
      <div className="trd-head">
        <div className="trd-titles">
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <a className="trd-view" href={viewAllHref}>
          View All
        </a>
      </div>

      <div className="trd-carousel-container">
        <button
          className="trd-nav-btn trd-nav-prev"
          onClick={goToPrev}
          disabled={page === 0}
          aria-label="Previous page"
        >
          <ChevronLeftIcon />
        </button>

        <div
          className="trd-viewport"
          ref={viewportRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="trd-track" style={{ width: `${slides.length * 100}%` }}>
            {slides.map((pageItems, i) => (
              <div
                className="trd-slide"
                key={i}
                style={{ width: `${100 / slides.length}%` }}
              >
                <div
                  className="trd-grid"
                  style={{ "--cols": pageItems.length || perPage }}
                >
                  {pageItems.map((p) => (
                    <article
                      className={`trd-card ${p.__sk !== undefined ? "skel" : ""}`}
                      key={p.__sk ?? p.id}
                      onClick={() => p.__sk === undefined && navigate(`/product/${p.id}`)}
                      style={{ cursor: p.__sk === undefined ? "pointer" : "default" }}
                    >
                      <div className="trd-img">
                        {p.__sk === undefined && p.image_url ? (
                          <img
                            src={p.image_url}
                            alt={p.name}
                            loading="lazy"
                            decoding="async"
                          />
                        ) : null}
                      </div>
                      <div className="trd-body">
                        <div className="trd-name">
                          {p.__sk !== undefined ? "\u00A0" : p.name}
                        </div>
                        <div className="trd-price">
                          {p.__sk !== undefined ? "\u00A0" : (
                            <>
                              {p.variants && Array.isArray(p.variants) && p.variants.length > 0 ? (
                                <span className="trd-selling-price">
                                  {(() => {
                                    const prices = p.variants
                                      .map((v) => Number(v.selling_price || v.price || 0))
                                      .filter((val) => val > 0);
                                    if (prices.length === 0) return money(p.selling_price);
                                    const minPrice = Math.min(...prices);
                                    const maxPrice = Math.max(...prices);
                                    return minPrice !== maxPrice
                                      ? `${money(minPrice)} - ${money(maxPrice)}`
                                      : money(minPrice);
                                  })()}
                                </span>
                              ) : (
                                <>
                                  {p.regular_price > 0 &&
                                    p.regular_price !== p.selling_price && (
                                      <span className="trd-regular-price">
                                        {money(p.regular_price)}
                                      </span>
                                    )}
                                  <span className="trd-selling-price">
                                    {money(p.selling_price)}
                                  </span>
                                </>
                              )}
                            </>
                          )}
                        </div>
                        <button
                          className="trd-cart"
                          aria-label={busyId === p.id ? "Adding..." : "Add to cart"}
                          onClick={(e) => {
                            e.stopPropagation();
                            p.__sk === undefined && handleAddToCart(p.id, 1);
                          }}
                          disabled={busyId === p.id}
                          title={busyId === p.id ? "Adding..." : "Add to cart"}
                        >
                          {busyId === p.id ? <Loader size={18} /> : <CartIcon />}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          className="trd-nav-btn trd-nav-next"
          onClick={goToNext}
          disabled={page === slides.length - 1}
          aria-label="Next page"
        >
          <ChevronRightIcon />
        </button>
      </div>

      <div className="trd-dots" role="tablist" aria-label="Hot offers pages">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`trd-dot ${i === page ? "active" : ""}`}
            onClick={() => goTo(i)}
            role="tab"
            aria-selected={i === page}
            aria-label={`Go to page ${i + 1}`}
          />
        ))}
      </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
}

function money(n = 0) {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n || 0);
  } catch {
    return `$${(n || 0).toFixed(2)}`;
  }
}

function ChevronLeftIcon({ size = 24, strokeWidth = 2, color = "#333" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M15 18l-6-6 6-6"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CartIcon({ size = 18, strokeWidth = 1.8, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <line
        x1="12"
        y1="4"
        x2="12"
        y2="8"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <line
        x1="10"
        y1="6"
        x2="14"
        y2="6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path
        d="M6 8h13l-1.2 6.5a2 2 0 0 1-2 1.5H8a2 2 0 0 1-2-1.5L4 4H2"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="20" r="1.6" fill={color} />
      <circle cx="18" cy="20" r="1.6" fill={color} />
    </svg>
  );
}

function ChevronRightIcon({ size = 24, strokeWidth = 2, color = "#333" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M9 18l6-6-6-6"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
